import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractNewSurveyComponent } from '@unity/abstract/abstract-new-survey.component';
import {
  AgreementsClient,
  ClientExpenseCreateDto,
  ClientExpensesClient,
  CreatedResponseDto,
  FormDataDto,
  TransitionMiniDto,
} from '@unity/clients';
import { PageContainerComponent } from '@unity/components/layouts/page-container/page-container.component';
import { IconService } from '@unity/services/icon.service';
import { SurveyDataService } from '@unity/services/survey-data.service';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardNewSurveySharedComponent } from '@unity/shared/composed/card-new-survey-shared/card-new-survey-shared.component';
import { QuickActions } from '@unity/utils/quick-actions';
import { UnityRouteUrlTrees } from '@unity/utils/types';
import { EMPTY } from 'rxjs';
import { SurveyModule } from 'survey-angular-ui';

@Component({
  selector: 'unity-new-client-expense-page',
  imports: [KENDO_BUTTON, KENDO_STACKLAYOUT, SurveyModule, PageContainerComponent, CardNewSurveySharedComponent, ActionSharedComponent],
  templateUrl: './new-client-expense-page.component.html',
  styleUrl: './new-client-expense-page.component.scss',
})
export class NewClientExpensePageComponent extends AbstractNewSurveyComponent<CreatedResponseDto> {
  private readonly agreementsClientExpense = inject(AgreementsClient);
  private readonly clientExpensesClientExpense = inject(ClientExpensesClient);
  private readonly iconService = inject(IconService);
  private readonly surveyDataService = inject(SurveyDataService);

  protected saveIcon = this.iconService.saveIcon;

  /* Inputs */
  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });

  /* Impl */
  protected override redirectUrlFn: (response: CreatedResponseDto) => UnityRouteUrlTrees = (response: CreatedResponseDto) => {
    const agreementId = this.$agreementId();
    return ['/agreement', agreementId, 'client-expense', response.id];
  };

  protected override cancelUrlFn = (): UnityRouteUrlTrees => {
    const agreementId = this.$agreementId();
    return ['/agreement', agreementId, 'client-expense'];
  };

  protected override readonly $formSchema = computed(() => {
    const agreement = this.agreementResource.value();
    return agreement?.client_expense_form_schema;
  });

  /* Resources */
  protected readonly agreementResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      return agreementId ? { agreementId } : undefined;
    },
    stream: ({ params: { agreementId } }) => this.agreementsClientExpense.getOneAsDetailed(agreementId),
  });

  /* Signals */
  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));

  protected readonly $contentHeader = signal('Create Client Expense');

  protected readonly $contentHeaderSecondary = computed(() => {
    const agreementResource = this.agreementResource.value();
    return `Agreement: ${agreementResource?.name}`;
  });

  public constructor() {
    super();

    effect(() => {
      const agreementId = this.$agreementId();
      this.surveyDataService.setAgreementId(agreementId);
    });
  }

  /* Methods */
  protected override createResourceFn$ = (data: FormDataDto, transition: TransitionMiniDto) => {
    const agreementId = untracked(this.$agreementId);

    if (!agreementId) {
      return EMPTY;
    }

    const createDto: ClientExpenseCreateDto = {
      agreement_id: agreementId,
      initial_transition_id: transition.id,
      client_id: data['client_id'],
      ...data,
    };

    return this.clientExpensesClientExpense.createOneForAgreement(agreementId, createDto);
  };
}
